import { buildScenarioConfigurationPayload } from '../../../../pages/sessions/new/utils/scenarioForm/transform';
import type { ScenarioConfigFormValues } from '../../../../pages/sessions/new/forms/ScenarioForm/formSchema';
import type { CreateConfigurationRequest } from '@types';
import type { ExposureItemCategory } from '../../../../data/foods';
import { getExposureItemIdsByCategories } from '../../../../data/foods';
import { EXPOSURE_MULTI_ITEM_GROUP_SIZE } from '../../../../utils/exposureMultiItem';
import type {
  BodyImageQuickFormState,
  ExposureQuickFormState,
  PerformanceQuickFormState,
  QuickConfigAppType,
  QuickConfigDevices,
} from '../types';

export type BuildQuickPayloadInput = {
  appType: QuickConfigAppType;
  patientId: string;
  devices: QuickConfigDevices;
  exposure?: ExposureQuickFormState;
  performance?: PerformanceQuickFormState;
  bodyImage?: BodyImageQuickFormState;
};

const selectedCategories = (
  categories: ExposureQuickFormState['categories']
): ExposureItemCategory[] =>
  (Object.keys(categories) as ExposureItemCategory[]).filter(
    key => categories[key]
  );

const toFormValues = (
  input: BuildQuickPayloadInput
): ScenarioConfigFormValues => {
  if (input.appType === 'exposure') {
    if (!input.exposure) {
      throw new Error('Config Exposure incompleta');
    }
    const cats = selectedCategories(input.exposure.categories);
    if (cats.length === 0) {
      throw new Error('Seleziona almeno una categoria di elementi');
    }
    const itemIds = getExposureItemIdsByCategories(cats);
    if (itemIds.length === 0) {
      throw new Error('Nessun elemento trovato per le categorie selezionate');
    }

    return {
      scenarioType: 'exposure',
      exposure: {
        map: input.exposure.map,
        itemIds,
        multiItem: input.exposure.multiItem,
        hasTutorial: input.exposure.hasTutorial,
        userSpawnPosition: input.exposure.userSpawnPosition || '1',
      },
    };
  }

  if (input.appType === 'performance') {
    if (!input.performance) {
      throw new Error('Config Performance incompleta');
    }
    return {
      scenarioType: 'performance',
      performance: {
        map: input.performance.map,
        currentState: input.performance.currentState,
        variation: input.performance.variation,
        duration: input.performance.duration,
      },
    };
  }

  return {
    scenarioType: 'body_image',
    bodyImage: {},
  };
};

export const buildQuickPayload = (
  input: BuildQuickPayloadInput
): CreateConfigurationRequest => {
  if (!input.patientId) {
    throw new Error('Paziente della sessione corrente non disponibile');
  }
  if (!input.devices.deviceId) {
    throw new Error('Seleziona un visore');
  }

  return buildScenarioConfigurationPayload({
    formValues: toFormValues(input),
    patientId: input.patientId,
    deviceId: input.devices.deviceId,
    sensorId: input.devices.sensorId,
  });
};

/** Preview counts for Exposure UI (before create). */
export const previewExposureItemStats = (
  exposure: ExposureQuickFormState
): { itemCount: number; groupCount: number | null } => {
  const itemIds = getExposureItemIdsByCategories(
    selectedCategories(exposure.categories)
  );
  if (!exposure.multiItem) {
    return { itemCount: itemIds.length, groupCount: null };
  }
  return {
    itemCount: itemIds.length,
    groupCount:
      itemIds.length === 0
        ? 0
        : Math.ceil(itemIds.length / EXPOSURE_MULTI_ITEM_GROUP_SIZE),
  };
};
